import { type ReactNode, useState } from 'react'
import { useQuery } from '@apollo/client/react'
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Languages,
  MapPin,
  ShieldCheck,
  Sparkles,
  Star,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import {
  PROVIDER_PUBLIC_PROFILE_QUERY,
  type ProviderProfileAvailabilitySlot,
  type ProviderProfileServiceOffering,
  type ProviderPublicProfile,
  type ProviderPublicProfileData,
  type ProviderPublicProfileVariables,
} from '../graphql/providerProfile'
import ReviewsList, { type ReviewSortOption } from '../components/reviews/ReviewsList'
import { useAuth } from '../contexts/AuthContext'
import { useLang, useScopedTranslations } from '../contexts/LangContext'
import { localized, normalizeLocale, type SupportedLocale } from '../lib/i18n'

const dayOrder = [1, 2, 3, 4, 5, 6, 0] as const

const translations = {
  el: {
    eyebrow: 'Προφίλ επαγγελματία',
    loadingTitle: 'Φορτώνουμε το δημόσιο προφίλ...',
    notFoundTitle: 'Δεν βρέθηκε επαγγελματίας',
    notFoundBody: 'Το προφίλ που ζήτησες δεν είναι διαθέσιμο ή ο σύνδεσμος δεν είναι σωστός.',
    backToSearch: 'Επιστροφή στην αναζήτηση',
    bookNow: 'Κάνε κράτηση',
    bookingUnavailable: 'Μη διαθέσιμο για online κράτηση',
    bookingUnavailableBody: 'Ο online προγραμματισμός δεν είναι διαθέσιμος αυτή τη στιγμή για αυτό το προφίλ.',
    instantBooking: 'Άμεση online κράτηση',
    verified: 'Επαληθευμένος',
    backgroundChecked: 'Έλεγχος ιστορικού',
    vacationBadge: 'Προσωρινά μη διαθέσιμος',
    vacationMessage: 'Οι online κρατήσεις είναι προσωρινά κλειστές για αυτόν τον επαγγελματία.',
    availableAgain: 'Διαθέσιμος ξανά',
    ratingLabel: 'Αξιολόγηση',
    reviewsLabel: 'κριτικές',
    completedJobs: 'ολοκληρωμένες δουλειές',
    yearsExperience: 'χρόνια εμπειρίας',
    yearExperience: 'χρόνο εμπειρίας',
    experienceUnavailable: 'Εμπειρία μη διαθέσιμη',
    languagesLabel: 'Γλώσσες',
    servicesTitle: 'Υπηρεσίες που προσφέρει',
    servicesBody: 'Οι τελικές διαθέσιμες ώρες επιβεβαιώνονται στο βήμα της κράτησης.',
    availabilityTitle: 'Εβδομαδιαία διαθεσιμότητα',
    availabilityBody: 'Έλεγξε τις ακριβείς διαθέσιμες ώρες στο booking flow πριν επιβεβαιώσεις.',
    serviceAreasTitle: 'Περιοχές εξυπηρέτησης',
    aboutTitle: 'Σχετικά με τον επαγγελματία',
    trustTitle: 'Στοιχεία εμπιστοσύνης',
    reviewsTitle: 'Κριτικές',
    reviewsBody: 'Διάβασε τι είπαν οι προηγούμενοι πελάτες και άλλαξε ταξινόμηση για να εντοπίσεις γρήγορα το είδος feedback που σε ενδιαφέρει.',
    noReviewsTitle: 'Δεν υπάρχουν ακόμη κριτικές',
    noReviewsBody: 'Ο πρώτος κύκλος αξιολογήσεων θα εμφανιστεί εδώ μόλις ολοκληρωθούν δουλειές.',
    noBio: 'Δεν έχει προστεθεί ακόμη περιγραφή.',
    noAvailability: 'Δεν υπάρχουν ακόμη ενεργές ώρες διαθεσιμότητας.',
    noServiceAreas: 'Δεν έχουν προστεθεί ακόμη περιοχές εξυπηρέτησης.',
    closed: 'Κλειστά',
    priceLabel: 'Τιμή',
    durationLabel: 'Διάρκεια',
    memberSince: 'Μέλος από',
    trustLevel: 'Επίπεδο εμπιστοσύνης',
    reliabilityScore: 'Σκορ αξιοπιστίας',
    areasCovered: 'Ταχ. κώδικες',
    reviewPlaceholder: 'Χωρίς γραπτό σχόλιο',
    notSpecified: 'Δεν έχει οριστεί',
    languageGreek: 'Ελληνικά',
    languageEnglish: 'Αγγλικά',
    day0: 'Κυρ',
    day1: 'Δευ',
    day2: 'Τρι',
    day3: 'Τετ',
    day4: 'Πεμ',
    day5: 'Παρ',
    day6: 'Σαβ',
    sortLabel: 'Ταξινόμηση',
    sortRecent: 'Πιο πρόσφατες',
    sortHighest: 'Υψηλότερη βαθμολογία',
    sortLowest: 'Χαμηλότερη βαθμολογία',
    loadMore: 'Φόρτωση περισσότερων',
  },
  en: {
    eyebrow: 'Provider profile',
    loadingTitle: 'Loading public profile...',
    notFoundTitle: 'Provider not found',
    notFoundBody: 'This profile is unavailable or the link is incorrect.',
    backToSearch: 'Back to search',
    bookNow: 'Book now',
    bookingUnavailable: 'Unavailable for online booking',
    bookingUnavailableBody: 'Online booking is not available for this profile right now.',
    instantBooking: 'Instant online booking',
    verified: 'Verified',
    backgroundChecked: 'Background checked',
    vacationBadge: 'Temporarily unavailable',
    vacationMessage: 'Online booking is temporarily paused for this provider.',
    availableAgain: 'Available again',
    ratingLabel: 'Rating',
    reviewsLabel: 'reviews',
    completedJobs: 'completed jobs',
    yearsExperience: 'years experience',
    yearExperience: 'year experience',
    experienceUnavailable: 'Experience unavailable',
    languagesLabel: 'Languages',
    servicesTitle: 'Services offered',
    servicesBody: 'Exact availability is confirmed in the booking flow.',
    availabilityTitle: 'Weekly availability',
    availabilityBody: 'Check exact open slots during booking before you confirm.',
    serviceAreasTitle: 'Service areas',
    aboutTitle: 'About the provider',
    trustTitle: 'Trust details',
    reviewsTitle: 'Reviews',
    reviewsBody: 'Read recent customer feedback and switch sorting to focus on the type of signal you care about.',
    noReviewsTitle: 'No reviews yet',
    noReviewsBody: 'The first published reviews will appear here once jobs are completed.',
    noBio: 'No bio added yet.',
    noAvailability: 'No active availability has been published yet.',
    noServiceAreas: 'No service areas have been added yet.',
    closed: 'Closed',
    priceLabel: 'Price',
    durationLabel: 'Duration',
    memberSince: 'Member since',
    trustLevel: 'Trust level',
    reliabilityScore: 'Reliability score',
    areasCovered: 'Postal codes',
    reviewPlaceholder: 'No written comment',
    notSpecified: 'Not specified',
    languageGreek: 'Greek',
    languageEnglish: 'English',
    day0: 'Sun',
    day1: 'Mon',
    day2: 'Tue',
    day3: 'Wed',
    day4: 'Thu',
    day5: 'Fri',
    day6: 'Sat',
    sortLabel: 'Sort by',
    sortRecent: 'Most recent',
    sortHighest: 'Highest rating',
    sortLowest: 'Lowest rating',
    loadMore: 'Load more',
  },
} as const

type ProviderCopy = Record<keyof typeof translations.en, string>

function SectionCard({ title, children, eyebrow }: { title: string; children: ReactNode; eyebrow?: string }) {
  return (
    <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{eyebrow}</p>}
      <h2 className="mt-2 text-2xl font-bold text-slate-950">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  )
}

function LoadingProfile() {
  return (
    <div className="mx-auto max-w-6xl animate-pulse space-y-8 px-4 py-8 md:px-6 md:py-10">
      <div className="rounded-[2rem] bg-slate-200 p-8 md:p-10">
        <div className="h-4 w-36 rounded-full bg-slate-300" />
        <div className="mt-5 h-12 max-w-md rounded-full bg-slate-300" />
        <div className="mt-4 h-6 max-w-2xl rounded-full bg-slate-300" />
        <div className="mt-8 flex flex-wrap gap-3">
          <div className="h-11 w-36 rounded-xl bg-slate-300" />
          <div className="h-11 w-44 rounded-xl bg-slate-300" />
        </div>
      </div>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-8">
          <div className="h-64 rounded-[1.75rem] bg-slate-200" />
          <div className="h-64 rounded-[1.75rem] bg-slate-200" />
        </div>
        <div className="space-y-8">
          <div className="h-80 rounded-[1.75rem] bg-slate-200" />
          <div className="h-48 rounded-[1.75rem] bg-slate-200" />
        </div>
      </div>
    </div>
  )
}

function providerDisplayName(provider: ProviderPublicProfile) {
  return `${provider.user.firstName} ${provider.user.lastName}`
}

function formatCurrency(cents: number | null, locale: SupportedLocale) {
  if (cents === null) return null

  return new Intl.NumberFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

function formatExperience(yearsExperience: number | null, copy: ProviderCopy) {
  if (yearsExperience === null) return copy.experienceUnavailable
  return `${yearsExperience} ${yearsExperience === 1 ? copy.yearExperience : copy.yearsExperience}`
}

function formatMemberSince(dateValue: string, locale: SupportedLocale) {
  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(dateValue))
}

function formatVacationUntil(dateValue: string, locale: SupportedLocale) {
  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(`${dateValue}T12:00:00`))
}

function languageLabel(code: string, copy: ProviderCopy) {
  if (code === 'el') return copy.languageGreek
  if (code === 'en') return copy.languageEnglish
  return code.toUpperCase()
}

function formatTrustLevel(level: string, locale: SupportedLocale) {
  const labels = {
    el: {
      bronze: 'Bronze',
      silver: 'Silver',
      gold: 'Gold',
      platinum: 'Platinum',
    },
    en: {
      bronze: 'Bronze',
      silver: 'Silver',
      gold: 'Gold',
      platinum: 'Platinum',
    },
  } as const

  const normalizedLevel = level.toLowerCase() as keyof typeof labels.en
  return labels[locale][normalizedLevel] ?? level
}

function normalizeTime(timeValue: string) {
  return timeValue.slice(0, 5)
}

function ratingLabel(value: number) {
  return value.toFixed(1)
}

function groupedServiceOfferings(serviceOfferings: ProviderProfileServiceOffering[], locale: SupportedLocale) {
  const grouped = new Map<string, { label: string; offerings: ProviderProfileServiceOffering[] }>()

  serviceOfferings.forEach((offering) => {
    const category = offering.serviceType.serviceCategory
    const key = category.id
    const label = localized(category, locale)
    const existing = grouped.get(key)

    if (existing) {
      existing.offerings.push(offering)
      return
    }

    grouped.set(key, { label, offerings: [offering] })
  })

  return Array.from(grouped.values()).sort((left, right) => left.label.localeCompare(right.label))
}

function scheduleForDay(dayOfWeek: number, slots: ProviderProfileAvailabilitySlot[]) {
  return slots
    .filter((slot) => slot.dayOfWeek === dayOfWeek)
    .sort((left, right) => left.startTime.localeCompare(right.startTime))
}

function dayLabel(dayOfWeek: number, copy: ProviderCopy) {
  return copy[`day${dayOfWeek}` as keyof Pick<ProviderCopy, 'day0' | 'day1' | 'day2' | 'day3' | 'day4' | 'day5' | 'day6'>]
}

export default function ProviderProfilePage() {
  const { id } = useParams()
  const { currentUser } = useAuth()
  const { locale: appLocale } = useLang()
  const activeLocale = normalizeLocale(currentUser?.locale ?? appLocale)
  const copy = useScopedTranslations('provider.profile', translations[activeLocale], activeLocale)
  const [reviewsLimit, setReviewsLimit] = useState(4)
  const [reviewsSortBy, setReviewsSortBy] = useState<ReviewSortOption>('RECENT')

  const { data, previousData, loading, error } = useQuery<ProviderPublicProfileData, ProviderPublicProfileVariables>(
    PROVIDER_PUBLIC_PROFILE_QUERY,
    {
      variables: {
        id: id ?? '',
        reviewsFirst: reviewsLimit,
        reviewsSortBy,
      },
      skip: !id,
      notifyOnNetworkStatusChange: true,
    },
  )

  const provider = data?.providerProfile ?? previousData?.providerProfile

  if (loading && !provider) return <LoadingProfile />

  if (error || !provider) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
        <SectionCard title={copy.notFoundTitle}>
          <p className="text-sm leading-7 text-slate-600">{copy.notFoundBody}</p>
          <div className="mt-6">
            <Link to="/search" className="text-sm font-semibold text-primary-600 hover:underline">
              {copy.backToSearch}
            </Link>
          </div>
        </SectionCard>
      </div>
    )
  }

  const canBookNow = provider.serviceOfferings.length > 0 && provider.availabilitySlots.length > 0 && provider.serviceAreas.length > 0
  const bookingEnabled = canBookNow && !provider.onVacation
  const groupedOfferings = groupedServiceOfferings(provider.serviceOfferings, activeLocale)
  const priceLabelValue = formatCurrency(provider.hourlyRateCents, activeLocale)

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <section className="overflow-hidden rounded-[2rem] bg-[linear-gradient(135deg,#0f172a_0%,#102542_55%,#164e63_100%)] p-8 text-white shadow-[0_28px_70px_-40px_rgba(15,23,42,0.9)] md:p-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200">{copy.eyebrow}</p>
            <div className="mt-6 flex items-start gap-5">
              {provider.user.avatarUrl ? (
                <img
                  src={provider.user.avatarUrl}
                  alt=""
                  className="h-24 w-24 rounded-[1.5rem] border border-white/15 object-cover shadow-lg"
                />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-[1.5rem] border border-white/15 bg-white/10 text-2xl font-black shadow-lg">
                  {provider.user.firstName.charAt(0)}
                  {provider.user.lastName.charAt(0)}
                </div>
              )}

              <div className="min-w-0 flex-1">
                <h1 className="text-4xl font-black tracking-tight text-white sm:text-5xl">
                  {providerDisplayName(provider)}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {(provider.verified || provider.verifiedBadge) && (
                    <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-4 py-2 text-sm font-semibold text-emerald-100 ring-1 ring-emerald-300/20">
                      <BadgeCheck className="h-4 w-4" />
                      {copy.verified}
                    </span>
                  )}
                  {provider.backgroundChecked && (
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-slate-100 ring-1 ring-white/10">
                      <ShieldCheck className="h-4 w-4" />
                      {copy.backgroundChecked}
                    </span>
                  )}
                  {provider.onVacation && (
                    <span className="inline-flex items-center gap-2 rounded-full bg-rose-400/15 px-4 py-2 text-sm font-semibold text-rose-100 ring-1 ring-rose-200/20">
                      <CalendarDays className="h-4 w-4" />
                      {copy.vacationBadge}
                    </span>
                  )}
                  {provider.instantBooking && (
                    <span className="inline-flex items-center gap-2 rounded-full bg-amber-300/15 px-4 py-2 text-sm font-semibold text-amber-100 ring-1 ring-amber-200/20">
                      <Sparkles className="h-4 w-4" />
                      {copy.instantBooking}
                    </span>
                  )}
                </div>

                <p className="mt-6 max-w-3xl text-base leading-8 text-slate-200">
                  {provider.bio || copy.noBio}
                </p>
              </div>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-[1.35rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">{copy.ratingLabel}</p>
                <p className="mt-3 inline-flex items-center gap-2 text-2xl font-black text-white">
                  <Star className="h-5 w-5 fill-amber-300 text-amber-300" />
                  {ratingLabel(provider.averageRating)}
                </p>
                <p className="mt-2 text-sm text-slate-200">{provider.totalReviews} {copy.reviewsLabel}</p>
              </div>

              <div className="rounded-[1.35rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">{copy.priceLabel}</p>
                <p className="mt-3 text-2xl font-black text-white">
                  {priceLabelValue ?? copy.bookingUnavailable}
                </p>
                {priceLabelValue && (
                  <p className="mt-2 text-sm text-slate-200">/{activeLocale === 'el' ? 'ώρα' : 'hr'}</p>
                )}
              </div>

              <div className="rounded-[1.35rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                  {copy.languagesLabel}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {provider.languages.map((language) => (
                    <span key={language} className="rounded-full bg-white/10 px-3 py-1.5 text-sm font-semibold text-white">
                      {languageLabel(language, copy)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-[1.35rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                  {copy.completedJobs}
                </p>
                <p className="mt-3 text-2xl font-black text-white">{provider.completedJobs}</p>
                <p className="mt-2 text-sm text-slate-200">{formatExperience(provider.yearsExperience, copy)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-white/10 bg-white/10 p-6 backdrop-blur-sm">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-200">{copy.bookNow}</p>
            <p className="mt-4 text-sm leading-7 text-slate-200">
              {provider.onVacation ? copy.vacationMessage : bookingEnabled ? copy.servicesBody : copy.bookingUnavailableBody}
            </p>
            {provider.onVacation && provider.vacationUntil && (
              <p className="mt-3 text-sm font-semibold text-rose-100">
                {copy.availableAgain}: {formatVacationUntil(provider.vacationUntil, activeLocale)}
              </p>
            )}
            <div className="mt-6 space-y-3">
              {bookingEnabled ? (
                <Link
                  to={`/book/${provider.id}`}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-6 py-3 text-base font-semibold text-white transition hover:bg-primary-700"
                >
                  {copy.bookNow}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex w-full items-center justify-center rounded-xl bg-primary-200 px-6 py-3 text-base font-semibold text-white"
                >
                  {copy.bookingUnavailable}
                </button>
              )}
              <Link
                to="/search"
                className="inline-flex w-full items-center justify-center rounded-xl border border-white/15 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                {copy.backToSearch}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-8">
          <SectionCard title={copy.servicesTitle} eyebrow={copy.eyebrow}>
            <p className="text-sm leading-7 text-slate-600">{copy.servicesBody}</p>

            <div className="mt-6 space-y-5">
              {groupedOfferings.map((group) => (
                <div key={group.label} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <h3 className="text-lg font-bold text-slate-950">{group.label}</h3>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {group.offerings.map((offering) => {
                      const serviceLabel = localized(offering.serviceType, activeLocale)
                      const price = formatCurrency(offering.customPriceCents ?? offering.serviceType.basePriceCents, activeLocale)
                      const duration = offering.customDurationMinutes ?? offering.serviceType.estimatedDurationMinutes

                      return (
                        <article key={offering.id} className="rounded-[1.25rem] border border-white bg-white p-4 shadow-sm">
                          <h4 className="text-base font-bold text-slate-950">{serviceLabel}</h4>
                          <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-600">
                            <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1.5 font-semibold text-primary-700">
                              <BriefcaseBusiness className="h-4 w-4" />
                              {price}
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-700">
                              <Clock3 className="h-4 w-4" />
                              {duration} {activeLocale === 'el' ? 'λεπτά' : 'min'}
                            </span>
                          </div>
                        </article>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title={copy.availabilityTitle}>
            <div className="flex items-start gap-3 rounded-[1.25rem] bg-slate-50 px-4 py-4 text-sm leading-7 text-slate-600">
              <CalendarDays className="mt-1 h-4 w-4 shrink-0 text-primary-600" />
              <p>{copy.availabilityBody}</p>
            </div>

            {provider.availabilitySlots.length === 0 ? (
              <p className="mt-6 rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                {copy.noAvailability}
              </p>
            ) : (
              <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {dayOrder.map((day) => {
                  const daySlots = scheduleForDay(day, provider.availabilitySlots)

                  return (
                    <article key={day} className="rounded-[1.25rem] border border-slate-200 bg-white p-4">
                      <p className="text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
                        {dayLabel(day, copy)}
                      </p>
                      <div className="mt-4 space-y-2">
                        {daySlots.length === 0 ? (
                          <p className="text-sm text-slate-400">{copy.closed}</p>
                        ) : (
                          daySlots.map((slot) => (
                            <div key={slot.id} className="rounded-xl bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-700">
                              {normalizeTime(slot.startTime)} - {normalizeTime(slot.endTime)}
                            </div>
                          ))
                        )}
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard title={copy.reviewsTitle}>
            <ReviewsList
              averageRating={provider.averageRating}
              totalReviews={provider.totalReviews}
              breakdown={provider.ratingBreakdown}
              reviews={provider.reviews.nodes}
              loading={loading}
              hasNextPage={provider.reviews.pageInfo.hasNextPage}
              sortBy={reviewsSortBy}
              onSortChange={(nextValue) => {
                setReviewsSortBy(nextValue)
                setReviewsLimit(4)
              }}
              onLoadMore={() => setReviewsLimit((current) => current + 4)}
              copy={{
                reviewsBody: copy.reviewsBody,
                reviewsLabel: copy.reviewsLabel,
                noReviewsTitle: copy.noReviewsTitle,
                noReviewsBody: copy.noReviewsBody,
                reviewPlaceholder: copy.reviewPlaceholder,
                sortLabel: copy.sortLabel,
                sortRecent: copy.sortRecent,
                sortHighest: copy.sortHighest,
                sortLowest: copy.sortLowest,
                loadMore: copy.loadMore,
              }}
            />
          </SectionCard>
        </div>

        <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
          <SectionCard title={copy.aboutTitle}>
            <div className="space-y-5 text-sm leading-7 text-slate-600">
              <p>{provider.bio || copy.noBio}</p>

              <div className="grid gap-3">
                <div className="flex items-start gap-3 rounded-[1.25rem] bg-slate-50 px-4 py-4">
                  <Languages className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-950">{copy.languagesLabel}</p>
                    <p className="mt-1">
                      {provider.languages.length > 0
                        ? provider.languages.map((language) => languageLabel(language, copy)).join(', ')
                        : copy.notSpecified}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-[1.25rem] bg-slate-50 px-4 py-4">
                  <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-950">{copy.completedJobs}</p>
                    <p className="mt-1">{provider.completedJobs}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-[1.25rem] bg-slate-50 px-4 py-4">
                  <CalendarDays className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-950">{copy.memberSince}</p>
                    <p className="mt-1">{formatMemberSince(provider.trustScore.memberSince, activeLocale)}</p>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard title={copy.trustTitle}>
            <div className="space-y-3">
              <div className="rounded-[1.25rem] bg-primary-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">{copy.trustLevel}</p>
                <p className="mt-2 text-2xl font-black text-primary-900">{formatTrustLevel(provider.trustScore.level, activeLocale)}</p>
              </div>
              <div className="rounded-[1.25rem] bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{copy.reliabilityScore}</p>
                <p className="mt-2 text-2xl font-black text-slate-950">{provider.trustScore.reliabilityScore.toFixed(1)}%</p>
              </div>
            </div>
          </SectionCard>

          <SectionCard title={copy.serviceAreasTitle}>
            {provider.serviceAreas.length === 0 ? (
              <p className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                {copy.noServiceAreas}
              </p>
            ) : (
              <div className="grid gap-3">
                {provider.serviceAreas.map((area) => (
                  <article key={area.id} className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="mt-1 h-4 w-4 shrink-0 text-primary-600" />
                      <div>
                        <p className="text-sm font-bold text-slate-950">
                          {[area.areaName, area.city].filter(Boolean).join(', ')}
                        </p>
                        <p className="mt-1 text-sm text-slate-500">{copy.areasCovered}: {area.postalCode}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </SectionCard>
        </aside>
      </div>
    </div>
  )
}
